import './App.css';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import Mustache from 'mustache';
// import Geo from './components/geo.js';
import Form from './components/form.js';
import Stripe from './components/stripe.js';
import Landing from './components/Landing.js';
import Tracking from './components/Tracking.js';
import PdfReady from './components/PdfReady.js';
import { track } from './analytics.js';

const POSTCARD_SIZES = {
  '4x6':  { height: 4, width: 6,  metric: '10x15cm' },
  '6x9':  { height: 6, width: 9,  metric: '15x23cm' },
  '6x11': { height: 6, width: 11, metric: '15x28cm' },
};

// A postcard-shaped card holding either the front artwork or the back mockup.
function PostcardCard({ size, children }) {
  const { height, width } = POSTCARD_SIZES[size] || POSTCARD_SIZES['4x6'];
  const aspect = `${(height / width) * 100}%`;
  return (
    <div className="pc-card" style={{ paddingBottom: aspect }}>
      <div className="pc-card-inner">{children}</div>
    </div>
  );
}

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

// Overlay injected into the template's #ink-free zone (bottom-right, the area
// where Lob prints the address). The recipient name fills in live as it's
// typed; the address itself is only known after verification and is printed by
// Lob, so we show a note there rather than a half-populated block.
function addressOverlay(recipientName) {
  const name = recipientName ? esc(recipientName) + '<br>' : '';
  const note = '<span style="color:#aaa;font-style:italic;">Address printed here at mailing</span>';
  return (
    '<div style="position:absolute;top:0.7in;left:0.4in;font-family:Arial,Helvetica,sans-serif;font-size:12px;line-height:1.5;color:#000;">' + name + note + '</div>' +
    '<div style="position:absolute;top:0.22in;right:0.22in;width:0.72in;height:0.56in;border:1px dashed #cfcfcf;border-radius:3px;color:#c4c4c4;font:7px Arial;text-align:center;line-height:0.56in;letter-spacing:.05em;">STAMP</div>'
  );
}

// Scales the 6.25x4.25in (600x408px) template body to fit the card width.
function BackFrame({ html }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(0);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setScale(el.clientWidth / 600);
    update();
    let ro;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(update); ro.observe(el); }
    window.addEventListener('resize', update);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', update); };
  }, []);
  return (
    <div ref={wrapRef} className="pc-frame" style={{ height: scale ? 408 * scale : 0 }}>
      <iframe title="Postcard back preview" sandbox="" scrolling="no" srcDoc={html}
        style={{ position: 'absolute', top: 0, left: 0, width: 600, height: 408, border: 0,
                 transform: `scale(${scale})`, transformOrigin: 'top left' }} />
    </div>
  );
}

// Renders the ACTUAL Lob back template (fetched from the backend) with the
// buyer's merge variables substituted via Mustache, so the preview matches
// what gets printed. Recipient address is overlaid into the template's
// address zone and updates live as the form is filled.
function PostcardBack({ size, artistUrl, nonce, recipientName, params = {} }) {
  const [tmpl, setTmpl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!nonce) return undefined;
    let cancelled = false;
    fetch(process.env.REACT_APP_BACKEND_URL + '/template-preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nonce, artistUrl, size }),
    })
      .then((r) => r.json())
      .then((d) => { if (!cancelled) { d.result === 'success' ? setTmpl(d.html) : setFailed(true); } })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [nonce, artistUrl, size]);

  const html = useMemo(() => {
    if (!tmpl) return null;
    const p = params || {};
    const mv = {
      artworkTitle:  p.artworkTitle  ?? p.title ?? '',
      artworkArtist: p.artworkArtist ?? p.attribution ?? p.artist ?? '',
      artworkYear:   p.artworkYear   ?? p.date ?? p.year ?? '',
      artworkMuseum: p.artworkMuseum ?? p.source ?? p.museum ?? '',
      userMessage:   p.userMessage   ?? p.message ?? '',
      footerHeader:  p.footerHeader  ?? '',
      footerMessage: p.footerMessage ?? '',
      footerUrl:     p.footerUrl     ?? '',
      qrCodeUrl:     p.qrCodeUrl     ?? artistUrl ?? '',
    };
    let out = Mustache.render(tmpl, mv);
    out = out.replace('<div id="ink-free">', '<div id="ink-free">' + addressOverlay(recipientName));
    return out;
  }, [tmpl, params, recipientName, artistUrl]);

  if (failed) {
    return <PostcardCard size={size}><div className="pcback-fallback">Back preview unavailable</div></PostcardCard>;
  }
  if (!html) {
    return <PostcardCard size={size}><div className="pcback-fallback">Loading back…</div></PostcardCard>;
  }
  return <BackFrame html={html} />;
}

// Sitewide footer: copyright + Privacy/Terms links. Used on landing,
// checkout (main), success, cancel, and tracking views.
function Footer() {
  const linkStyle = { color: 'inherit', textDecoration: 'underline' };
  const sepStyle = { color: '#ccc', margin: '0 4px' };
  return (
    <footer style={{
      marginTop: '60px',
      padding: '24px 20px',
      borderTop: '1px solid #eee',
      textAlign: 'center',
      fontSize: '13px',
      color: '#888',
      fontWeight: 300,
    }}>
      <a href="/" style={linkStyle}>Ollie Mail</a> is a project from Vague Media LLC &copy; {new Date().getFullYear()}{' '}
      <span style={sepStyle}>·</span>{' '}
      <a href="/privacy" style={linkStyle}>Privacy</a>{' '}
      <span style={sepStyle}>·</span>{' '}
      <a href="/terms" style={linkStyle}>Terms</a>
    </footer>
  );
}

function App() {

  const [billingDetails, setBillingDetails] = useState({});
  const [recipientName, setRecipientName] = useState(''); // Name of person receiving postcard
  const [payerName, setPayerName] = useState(''); // Name of person paying
  const [email, setEmail] = useState('');
  const [addressVerified, setAddressVerified] = useState(false)
  const [paymentIntent, setPaymentIntent] = useState("");
  const [nonce, setNonce] = useState("");

  const urlSearchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const isSuccessPage = urlSearchParams.get('success') === 'true';
  const isCancelPage = urlSearchParams.get('cancel') === 'true';
  const trackId = urlSearchParams.get('track');
  const isTracking = !!trackId && !isSuccessPage && !isCancelPage;
  const pdfId = urlSearchParams.get('pdf');
  const isPdfRedirect = !!pdfId && !isSuccessPage && !isCancelPage && !isTracking;
  const isMainPage = !isSuccessPage && !isCancelPage && !isTracking && !isPdfRedirect;
  const imgUrl = urlSearchParams.get('imgUrl');
  const size = POSTCARD_SIZES[urlSearchParams.get('size')] ? urlSearchParams.get('size') : '4x6';
  // Merge variables the artist site passed through (message/title/attribution/
  // QR etc.) — used to render the live back-of-postcard preview at checkout.
  const optionalParams = useMemo(() => {
    const raw = urlSearchParams.get('optionalParams');
    if (!raw) return {};
    try { return JSON.parse(decodeURIComponent(raw)); } catch (e) { return {}; }
  }, [urlSearchParams]);
  // Visitors who land on the domain directly (no imgUrl) get the marketing
  // landing page instead of a broken postcard preview.
  const isLanding = isMainPage && !imgUrl;

  useEffect(() => {
    const fetchSecret = () => {
      if( nonce.length === 0 ){
        fetch(process.env.REACT_APP_BACKEND_URL + '/nonce', {
          method: "POST",
          mode: 'cors',
          credentials: 'same-origin',
          cache: 'no-cache',
          redirect: 'follow',
          headers: {
            'Access-Control-Allow-Origin': process.env.REACT_APP_FRONTEND_ORIGIN,
            "Content-Type": "application/json"
          }
        })
        .then(res => res.json())
        .then(data => {
          setNonce(data.nonce);
          
          const artistUrl = urlSearchParams.get('artistUrl');
          
          fetch(process.env.REACT_APP_BACKEND_URL + "/stripe", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              'Access-Control-Allow-Origin': process.env.REACT_APP_FRONTEND_ORIGIN
            },
            body: JSON.stringify({
              items: [{ id: "postcard-" + size }],
              nonce: data.nonce,
              artistUrl: artistUrl,
              size: size,
            }),
            mode: 'cors', // no-cors, *cors, same-origin
            credentials: 'same-origin', // include, *same-origin, omit
            cache: 'no-cache',
            redirect: 'follow'
          })
          .then(res => res.json())
          .then(data => {
            console.log('Initial payment intent response:', data);
            if (data.client_secret) {
              console.log('Payment intent created with client_secret:', data.client_secret);
              setPaymentIntent(data);
            } else {
              console.error('No client_secret in initial payment intent:', data);
            }
          })
          .catch(error => {
            console.error('Error creating initial payment intent:', error);
          });
        })
        .catch(error => {
          console.error(error);
        });
      }
    };

    if( isMainPage && !isLanding ){
      fetchSecret();
    }

  }, [nonce, isMainPage, isLanding, urlSearchParams, size]);

  // Funnel: top-of-funnel event when a visitor lands on the postcard maker
  // (i.e. arrived from an artist site with an imgUrl). Distinct from the
  // landing-page hit Umami records as a regular pageview.
  useEffect(() => {
    if (isMainPage && !isLanding) {
      track('postcard_started');
    }
  }, [isMainPage, isLanding]);

  if( isTracking ){
    return (
      <>
        <Tracking postcardId={trackId} />
        <Footer />
      </>
    );
  }

  if( isPdfRedirect ){
    return (
      <>
        <PdfReady postcardId={pdfId} />
        <Footer />
      </>
    );
  }

  if( isLanding ){
    return (
      <>
        <Landing />
        <Footer />
      </>
    );
  }

  if( isMainPage ){
    return (
      <div className="App">
        <div className="head">
          <h1>Send your postcard</h1>
          <div className="description">Fill out the form to send this artwork as a real {POSTCARD_SIZES[size].height} × {POSTCARD_SIZES[size].width} in ({POSTCARD_SIZES[size].metric}) postcard, printed and mailed anywhere you choose. Note: slight edge cropping can occur when art is printed on paper.</div>
        </div>
        <input type="hidden" name="nonce" value={nonce} />

        <div className="checkout-layout">
          <div className="checkout-preview">
              <div className="pc-caption">Front <span className="pc-caption-note">— {POSTCARD_SIZES[size].height} × {POSTCARD_SIZES[size].width} in ({POSTCARD_SIZES[size].metric})</span></div>
            <PostcardCard size={size}>
              <img className="pc-front-img" alt="Your generative artwork" src={imgUrl} />
            </PostcardCard>
            <div className="pc-caption">Back <span className="pc-caption-note">— preview; final print may vary</span></div>
            <PostcardBack
              size={size}
              artistUrl={urlSearchParams.get('artistUrl')}
              nonce={nonce}
              recipientName={recipientName}
              params={optionalParams}
            />
          </div>

          <div className="checkout-form">
            <Form
              postcardGenerated={true}
              setBillingDetails={setBillingDetails}
              recipientName={recipientName}
              setRecipientName={setRecipientName}
              addressVerified={addressVerified}
              setAddressVerified={setAddressVerified}
              size={size}
              />

            { paymentIntent.client_secret && <Stripe
                billingDetails={billingDetails}
                addressVerified={addressVerified}
                email={email}
                setEmail={setEmail}
                payerName={payerName}
                setPayerName={setPayerName}
                elementsOptions={{paymentIntent: paymentIntent, setPaymentIntent: setPaymentIntent}}
              /> }
          </div>
        </div>

        <Footer />
      </div>
    );
  }
  
  else if( isSuccessPage ){
    return (
      <>
        <div style={{textAlign: 'center'}} className="imagecontainer">
            <img className="imagecontainer-image" alt="Your generative artwork" src={imgUrl} style={{maxWidth: '760px'}} />
          </div>
        <div
        style={{
          maxWidth: '800px',
          margin: '0 auto',
          textAlign: 'center',
          padding: '20px',
        }}
        >
          Thank you for your order!
        </div>
        {(() => {
          const artistUrl = urlSearchParams.get('artistUrl');
          if (!artistUrl) return null;
          let domain;
          try { domain = new URL(artistUrl).hostname.replace(/^www\./, ''); }
          catch (e) { domain = artistUrl.replace(/^https?:\/\//, '').replace(/\/.*$/, ''); }
          return <a className="return-band" href={artistUrl}>Return to {domain} &rarr;</a>;
        })()}
        <Footer />
      </>
    );
  }
  else if( isCancelPage ){
    // Create a new URL object with the current URL
    const url = new URL(window.location.href);

    // Get the URLSearchParams object from the URL
    const urlSearchParams = url.searchParams;

    // Remove the 'cancel' parameter
    urlSearchParams.delete('cancel');

    // Update the URL object's search parameters
    url.search = urlSearchParams.toString();

    return (
      <>
        <div style={{textAlign: 'center'}} className="imagecontainer">
            <img className="imagecontainer-image" alt="Your generative artwork" src={imgUrl} style={{maxWidth: '760px'}} />
          </div>
        <div
        style={{
          maxWidth: '800px',
          margin: '0 auto',
          textAlign: 'center',
          padding: '20px',
        }}
        >
          Sorry your order canceled. If you want to try again click <a href={url.toString()}>here</a>.
        </div>
        <Footer />
      </>
    );
  }
}

export default App;